import { ActionScope } from "../../../flatbuffers/generated/archive/action-scope";
/**
 * Which car does a configured action belong to?
 *
 * `action_banks` is the button DEFINITION — one row per (command, bank) — while
 * `action_catalogs` holds one row per lift, carrying `lift_id` and
 * `target_position`. The seeder's own `PerCar_OneRowPerLift` test pins that
 * split: three keys across three lifts produce nine catalog rows against three
 * action_bank rows.
 *
 * The by-bank endpoint used to preload only the ONE catalog its `action_id`
 * pointed at, so cars 2..N were invisible. It now returns the siblings as
 * `lift_instances`, which is what makes per-car scoping possible here at all.
 *
 * THE RULE THAT MATTERS: an empty scope means "every lift in the bank", never
 * "no lift". A bank-level command — which is what `SeedBankControlActions`
 * writes and what the wizard shows as "Bank-Level" — carries no lift at all,
 * and reading that as "belongs to no car" would hide every hall call on the
 * screen. The predecessor of this code carried the same warning; it is repeated
 * because the failure is silent and total.
 */

/**
 * Which drawer a row belongs in, as the SERVER derived it from the key that
 * carries the bound command. Re-exported from the generated `Archive.ActionScope`
 * rather than restated, so a schema change breaks the compile instead of leaving
 * two numberings to drift apart silently.
 *
 * `UNKNOWN` is a real answer meaning "the server could not tell" — a row whose
 * command matched no adapter key, or a server that predates the field. It must
 * never be treated as a scope; the caller falls back to lift scoping there.
 */
export const ACTION_SCOPE_UNKNOWN = ActionScope.UNKNOWN;
export const ACTION_SCOPE_UNIT = ActionScope.UNIT;
export const ACTION_SCOPE_BANK = ActionScope.BANK;

/** A row's per-car instances, as the endpoint sends them. */
export interface LiftInstanceLike {
  liftId?: string | null;
  lift_id?: string | null;
}

/** The subset of an action row this module judges. */
export interface LiftScopedRow {
  liftIds?: string[] | null;
  action?: { liftId?: string | null } | null;
  /**
   * The server's answer to "which drawer?", from the bound command's KEY
   * encoding (`keys.units_based` / `keys.bank_level`). Both spellings are
   * declared because the JSON path is snake_case while the FlatBuffer object
   * API is camelCase, and this field crosses that boundary.
   */
  actionScope?: number | null;
  action_scope?: number | null;
}

/**
 * The server's scope verdict for a row, or undefined when it did not give one.
 *
 * Absent and `UNKNOWN` are the same thing and both yield undefined, so a caller
 * cannot accidentally treat "no answer" as a definite scope — the reason this
 * is an enum with a dedicated zero rather than a pair of booleans.
 */
export function serverScopeOf(row: LiftScopedRow): number | undefined {
  const raw = row.actionScope ?? row.action_scope;
  if (typeof raw !== "number" || raw === ACTION_SCOPE_UNKNOWN) return undefined;

  return raw;
}

/**
 * The lift ids an action is scoped to, empty when it applies to the whole bank.
 *
 * Reads `lift_instances` first, since that is the complete set, and falls back
 * to the single preloaded catalog's `liftId` so a server that predates the
 * `lift_instances` field still scopes to the one car it reports rather than
 * silently widening to all of them.
 */
export function liftIdsForRow(row: LiftScopedRow): string[] {
  const fromInstances = (row.liftIds ?? []).filter((id) => id.length > 0);
  if (fromInstances.length > 0) return fromInstances;

  const single = row.action?.liftId ?? "";

  return single ? [single] : [];
}

/**
 * Whether an action should render on the given car.
 *
 * Returns true when the action carries no lift scope — that is the bank-level
 * case, and it applies everywhere. Also returns true when no car is selected,
 * so a caller that has not resolved its lift yet shows the full set rather than
 * an empty panel.
 */
export function actionAppliesToLift(row: LiftScopedRow, liftId: string): boolean {
  if (!liftId) return true;

  // A command the server calls BANK addresses the group, so it belongs to the
  // bank drawer alone. Without this it would fall through to the empty-scope
  // branch below and render on every car as well as the bank.
  if (serverScopeOf(row) === ACTION_SCOPE_BANK) return false;

  const scope = liftIdsForRow(row);
  if (scope.length === 0) return true;

  return scope.includes(liftId);
}

/**
 * Whether an action is a BANK-level command — i.e. it carries no lift scope
 * at all, so it belongs on the bank's own Actions tab rather than any single
 * car's. This is the complement of a per-car row, never the complement of
 * "applies to the currently selected lift": a row scoped to lift A is neither
 * bank-level nor visible on lift B's tab, and this function correctly says
 * false for it on both.
 */
export function actionIsBankLevel(row: LiftScopedRow): boolean {
  // The server's verdict wins when it gave one. It answers how the VENDOR
  // encodes the command — a unit-based key addresses one car, a bank-level key
  // addresses the group — which is the question "which drawer?" actually asks.
  // Lift scoping answers a different one (which car the WIZARD picked), and a
  // unit-based command the wizard never scoped has no lift at all, which is why
  // reading only that put per-car buttons on the bank drawer.
  const scope = serverScopeOf(row);
  if (scope !== undefined) return scope === ACTION_SCOPE_BANK;

  return liftIdsForRow(row).length === 0;
}
